import { queryClient } from '@/lib/query-client.js';
import { type ReactNode, createContext, useCallback, useEffect, useRef, useState } from 'react';
import { type UserSummary, authClient } from '../api/auth-client.js';
import { configureApiClient } from '../api/client.js';
import { tokenStorage } from './token-storage.js';

export type AuthStatus = 'idle' | 'authenticating' | 'authenticated' | 'unauthenticated';

export interface AuthState {
  status: AuthStatus;
  user: UserSummary | null;
  error: string | null;
}

export interface AuthContextValue extends AuthState {
  exchange(erpToken: string): Promise<void>;
  logout(opts?: { revokeAll?: boolean }): Promise<void>;
  refresh(): Promise<void>;
  checkSession(): Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

function configureClient(): void {
  configureApiClient({
    tokenGetter: () => tokenStorage.getSessionToken(),
    refreshTokenGetter: () => tokenStorage.getRefreshToken(),
    tokenSetter: (sessionToken, refreshToken) => tokenStorage.save(sessionToken, refreshToken),
    tokenClearer: () => tokenStorage.clear(),
  });
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    status: 'idle',
    user: null,
    error: null,
  });

  const checkingRef = useRef(false);

  const refresh = useCallback(async (): Promise<void> => {
    const storedRefreshToken = tokenStorage.getRefreshToken();
    if (storedRefreshToken === null) return;

    const result = await authClient.refresh(storedRefreshToken);
    tokenStorage.save(result.sessionToken, result.refreshToken);
  }, []);

  const checkSession = useCallback(async (): Promise<void> => {
    const sessionToken = tokenStorage.getSessionToken();
    if (sessionToken === null) {
      setState({ status: 'unauthenticated', user: null, error: null });
      return;
    }

    try {
      const result = await authClient.check();
      setState({ status: 'authenticated', user: result.user, error: null });
    } catch {
      const storedRefresh = tokenStorage.getRefreshToken();
      if (storedRefresh === null) {
        tokenStorage.clear();
        setState({ status: 'unauthenticated', user: null, error: null });
        return;
      }

      try {
        const refreshed = await authClient.refresh(storedRefresh);
        tokenStorage.save(refreshed.sessionToken, refreshed.refreshToken);
        const result = await authClient.check();
        setState({ status: 'authenticated', user: result.user, error: null });
      } catch {
        tokenStorage.clear();
        setState({ status: 'unauthenticated', user: null, error: null });
      }
    }
  }, []);

  useEffect(() => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    configureClient();
    checkSession().catch(() => {
      setState({ status: 'unauthenticated', user: null, error: null });
    });
  }, [checkSession]);

  const exchange = useCallback(async (erpToken: string): Promise<void> => {
    setState((prev) => ({ ...prev, status: 'authenticating', error: null }));
    try {
      const result = await authClient.exchange(erpToken);
      tokenStorage.save(result.sessionToken, result.refreshToken);
      setState({ status: 'authenticated', user: result.user, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      tokenStorage.clear();
      setState({ status: 'unauthenticated', user: null, error: message });
      throw err;
    }
  }, []);

  const logout = useCallback(async (opts?: { revokeAll?: boolean }): Promise<void> => {
    try {
      await authClient.logout(opts);
    } finally {
      tokenStorage.clear();
      void queryClient.clear();
      setState({ status: 'unauthenticated', user: null, error: null });
    }
  }, []);

  const value: AuthContextValue = {
    ...state,
    exchange,
    logout,
    refresh,
    checkSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
